import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CheckResult } from '../api.ts';
import { getCheckNamespace, getRemediationKey } from '../i18n.ts';
import styles from './CheckRow.module.css';

interface CheckRowProps {
  result: CheckResult;
}

function observedCellClass(status: string, observed: string, rowStatus?: string): string {
  if (observed === 'N/A') return `${styles.detailValue} ${styles.detailNa}`;
  const effective = rowStatus ?? status;
  // Cap row highlight at the check's overall severity — a mismatch on a WARN check is orange, not red
  const display = (effective === 'fail' || effective === 'FAIL') && (status === 'WARN' || status === 'warn')
    ? 'WARN'
    : effective;
  switch (display) {
    case 'pass':
    case 'PASS': return `${styles.detailValue} ${styles.cellPass}`;
    case 'warn':
    case 'WARN': return `${styles.detailValue} ${styles.cellWarn}`;
    case 'fail':
    case 'FAIL': return `${styles.detailValue} ${styles.cellFail}`;
    case 'ERROR': return `${styles.detailValue} ${styles.cellError}`;
    default: return styles.detailValue;
  }
}

const statusIcons: Record<string, string> = {
  PASS: '✓',
  WARN: '!',
  FAIL: '✗',
  ERROR: '⚠',
  SKIPPED: '–',
  MUTED: '–',
};

export function CheckRow({ result }: CheckRowProps) {
  const ns = getCheckNamespace(result.checkId);
  const { t } = useTranslation([ns, 'ui']);
  // Muted checks run and show their real status but do not affect the overall QC result.
  const muted = result.severity === 'MUTED';
  const [expanded, setExpanded] = useState(
    !muted && result.status !== 'PASS' && result.status !== 'SKIPPED' && result.status !== 'MUTED',
  );

  const message = t(result.messageKey, result.messageParams);
  const description = t('description');

  // Resolve remediation text from error codes
  const remediationParts = result.errorCodes
    .map((code) => {
      const key = getRemediationKey(code);
      return key ? t(key, result.messageParams) : null;
    })
    .filter(Boolean);
  const remediation = remediationParts.length > 0 ? remediationParts.join(' ') : null;

  const statusClass = result.status.toLowerCase();

  return (
    <div className={`${styles.row} ${muted ? styles.mutedRow : ''}`}>
      <button
        className={styles.header}
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        <span className={`${styles.icon} ${muted ? styles.mutedIcon : styles[statusClass]}`}>
          {statusIcons[result.status]}
        </span>
        <span className={styles.name}>
          {result.displayName}
          {muted && <span className={styles.mutedBadge}>Muted</span>}
          <span className={styles.infoWrap} onClick={(e) => e.stopPropagation()}>
            <span className={styles.infoIcon}>i</span>
            <span className={styles.tooltip}>{description}</span>
          </span>
        </span>
        <span className={`${styles.status} ${muted ? styles.mutedStatus : styles[statusClass]}`}>
          {result.status}
        </span>
        <span className={styles.chevron}>{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div className={styles.detail}>
          {muted && (
            <p className={styles.mutedNote}>
              This check is muted — it runs for visibility but does not affect the overall QC result.
            </p>
          )}
          <p className={styles.message}>{message}</p>
          {result.detail.length > 0 && (() => {
            const hasExpected = result.detail.some((e) => e.expected);
            const hasExtra = result.detail.some((e) => e.extra);
            const hasRowStatus = result.detail.some((e) => e.rowStatus);
            const hasUrl = result.detail.some((e) => e.url);
            // Inventory mode: rowStatus colors the label cell (debt completeness)
            // Comparison mode: rowStatus colors the observed cell per-row (client info accuracy)
            const inventoryMode = hasRowStatus && !hasExpected;

            if (hasUrl) {
              const hasCompany = result.detail.some((e) => e.company);
              const hasAgent = result.detail.some((e) => e.agent);
              return (
                <div className={styles.detailTableWrap}>
                  <table className={styles.detailTable}>
                    <thead>
                      <tr>
                        <th className={styles.detailTh}>Name</th>
                        <th className={styles.detailTh}>DOB</th>
                        <th className={styles.detailTh}>Status</th>
                        <th className={styles.detailTh}>Stage</th>
                        {hasCompany && <th className={styles.detailTh}>Affiliate</th>}
                        {hasAgent && <th className={styles.detailTh}>Agent</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {result.detail.map((entry, i) => (
                        <tr key={i}>
                          <td className={styles.detailLabel}>
                            <a
                              href={entry.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.detailLink}
                            >
                              {entry.label}
                            </a>
                          </td>
                          <td className={styles.detailValue}>{entry.dob ?? '—'}</td>
                          <td className={styles.detailValue}>{entry.observed}</td>
                          <td className={styles.detailValue}>{entry.stage ?? '—'}</td>
                          {hasCompany && <td className={styles.detailValue}>{entry.company ?? '—'}</td>}
                          {hasAgent && <td className={styles.detailValue}>{entry.agent ?? '—'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }

            return (
              <div className={styles.detailTableWrap}>
                <table className={styles.detailTable}>
                  {inventoryMode ? (
                    <thead>
                      <tr>
                        <th className={styles.detailTh}>Creditor</th>
                        <th className={styles.detailTh}>Account #</th>
                        <th className={styles.detailTh}>Type</th>
                      </tr>
                    </thead>
                  ) : (
                    <thead>
                      <tr>
                        <th className={styles.detailTh}></th>
                        <th className={styles.detailTh}>Observed</th>
                        {hasExpected && <th className={styles.detailTh}>Expected</th>}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {result.detail.map((entry, i) => {
                      const labelClass = inventoryMode
                        ? (entry.rowStatus === 'pass' ? styles.detailLabelPass : styles.detailLabelFail)
                        : styles.detailLabel;
                      const observedClass = inventoryMode
                        ? styles.detailValue
                        : observedCellClass(result.status, entry.observed, entry.rowStatus);
                      return (
                        <tr key={i}>
                          <td className={labelClass}>{t(entry.label)}</td>
                          <td className={observedClass}>{entry.observed}</td>
                          {hasExtra && (
                            <td className={styles.detailValue}>{entry.extra ?? ''}</td>
                          )}
                          {hasExpected && (
                            <td className={styles.detailValue}>{entry.expected ?? ''}</td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
          {remediation && (
            <div className={result.status === 'WARN' ? styles.remediationWarn : styles.remediation}>
              <div className={styles.remediationLabel}>{t('ui:remediation.label')}</div>
              {remediation}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
